import * as Sentry from "@sentry/react";
import type { AnyRouter } from "@tanstack/react-router";
import { config } from "./config";
import { ApiError } from "./apiError";
import { appVersion } from "./version";
import { scrubDeep, scrubText } from "@carwash/popia-scrub";

// Re-exported: `sentry.test.ts` and any future consumer import it from here.
export { scrubText };

let enabled = false;

export function initSentry(router: AnyRouter): void {
  if (!config.sentryDsn) return; // blank DSN = fully disabled (dev default)
  enabled = true;
  Sentry.init({
    dsn: config.sentryDsn,
    release: appVersion.version, // the baked APP_VERSION
    environment: import.meta.env.MODE,
    sendDefaultPii: false,
    enableLogs: true,
    integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0),
    // Belt-and-braces: nothing phone-shaped leaves the app, whatever path it took.
    beforeSend: (event) => scrubDeep(event) as typeof event,
    beforeBreadcrumb: (crumb) => scrubDeep(crumb) as typeof crumb,
  });
}

export function captureApiFailure(err: unknown, context: { path: string }): void {
  // Expected 4xxs are UX states, not engineering signal (spec §7).
  if (err instanceof ApiError && err.status < 500) return;
  Sentry.captureException(err, { tags: { endpoint: context.path } });
}

export function logEvent(name: string, data?: Record<string, string | number | boolean>): void {
  const scrubbed = data ? (scrubDeep(data) as Record<string, string | number | boolean>) : undefined;
  Sentry.addBreadcrumb({ category: "app", message: name, data: scrubbed, level: "info" });
  // Sentry Logs search in this org indexes string attributes (closeReason showed
  // up; emptyFrames/videoWidth as integers did not). Keep breadcrumbs typed.
  const attrs = scrubbed
    ? Object.fromEntries(Object.entries(scrubbed).map(([k, v]) => [k, typeof v === "string" ? v : String(v)]))
    : undefined;
  Sentry.logger.info(name, attrs);
}

export function setSentryUser(customerId: string | null): void {
  if (!enabled) return;
  Sentry.setUser(customerId ? { id: customerId } : null); // pseudonymous Guid ONLY — never phone/uid
}
